import React, { useMemo, useState } from 'react';
import { renderCheck } from 'routes/Portfolio/Component/ActivityTab/TopicsTab/helper';
import Switch from 'react-switch';
import ToolTip from 'components/elements/atoms/ToolTip/ToolTip';
import { Link } from 'react-router-dom';
import getUserStatusDetail, {
  loggedStatusMap,
  statusMap,
} from './state.get.user.status.detail';
import { Icon, ICONS } from 'components/Icons';

export default function UserStatusPopoverContent({
  user,
  currentUser,
  updateUserProfile,
}) {
  const {
    userStatus,
    isLoggedIn,
    isPinnedIn,
    latestExtensions,
    loggedStatus,
    checkedInToday,
    isOwner,
    userName,
  } = getUserStatusDetail(user, currentUser);
  const latestVersion =
    (latestExtensions.find((ext) => ext.version) || {}).version || '-';
  const title = statusMap[userStatus].message;
  const icon = statusMap[userStatus].icon;

  return (
    <div className="d-flex flex-row align-items-center">
      {icon && (
        <div className="align-self-start mt-1 mr-3">
          <Icon icon={icon} size={16} className="popover-icon" />
        </div>
      )}
      <div className="d-flex flex-column flex-grow-1">
        <div className="d-flex flex-row align-items-center justify-content-end">
          {title && (
            <div className="flex-fill popover-main-title mr-3">{title}</div>
          )}
        </div>
        <div className="mt-2">
          <div className="division"></div>
          <div className="row pb-2 goal-header font-weight-600">
            <div className="col-md-12">Browser extension status</div>
          </div>

          <ExtensionStatusRow
            title="Logged in"
            condition={isLoggedIn.length > 0}
            linkTo="/help-page?helpId=679a5ac8d0617161e7c7a265"
            action={isLoggedIn.length > 0 ? 'logged into' : 'log in'}
            userName={userName}
            extensionName={[
              ...new Set(isLoggedIn.map((ext) => ext.extension)),
            ].join(', ')}
            latestExtensions={latestExtensions}
          />
          <ExtensionStatusRow
            title="Pinned"
            condition={isPinnedIn.length > 0}
            linkTo="/help-page?helpId=679a5b40d0617161e7c7b1fb"
            action={isPinnedIn.length > 0 ? 'pinned' : 'pin'}
            userName={userName}
            extensionName={[
              ...new Set(isPinnedIn.map((ext) => ext.extension)),
            ].join(', ')}
            latestExtensions={latestExtensions}
          />
          <StatusRow title="Checked in today" value={checkedInToday} />
          <StatusRow
            title="Latest version"
            value={latestVersion >= '0.0.126'}
          />

          <div className="row mt-1">
            <div className="col-md-9">
              <ul
                style={{
                  listStyle: 'none',
                  paddingLeft: '10px',
                  fontStyle: 'italic',
                  marginBottom: 0,
                }}
              >
                <li>{latestVersion}</li>
              </ul>
            </div>
          </div>

          <StatusRow
            title="Last article logged"
            value={loggedStatus === '24h'}
          />

          <div className="row mt-1">
            <div className="col-md-9">
              <ul
                style={{
                  listStyle: 'none',
                  paddingLeft: '10px',
                  fontStyle: 'italic',
                  marginBottom: 0,
                }}
              >
                <li>{loggedStatusMap[loggedStatus].message}</li>
              </ul>
            </div>
          </div>

          <div className="row mt-4 pb-2 goal-header font-weight-600">
            <div className="col-md-12">Features enabled</div>
          </div>

          {[
            {
              title: 'Analyze my reading',
              setting: user.profile.sourceSettingsAnalyzeAll,
              handleChange: (checked) =>
                updateUserProfile({ sourceSettingsAnalyzeAll: checked }),
            },
            {
              title: 'Credibility layer',
              setting: user.profile.sourceSettingsViewWithPoints,
              handleChange: (checked) =>
                updateUserProfile({ sourceSettingsViewWithPoints: checked }),
            },
            {
              title: 'Reading widget',
              setting: user.profile.sourceSettingsReadingWidget,
              handleChange: (checked) =>
                updateUserProfile({ sourceSettingsReadingWidget: checked }),
            },
            {
              title: 'Vocabulary',
              setting: user.profile.vocabularyEnabled,
              handleChange: (checked) =>
                updateUserProfile({ vocabularyEnabled: checked }),
            },
          ].map((feature, index) => (
            <FeatureRow key={index} isOwner={isOwner} {...feature} />
          ))}
        </div>
      </div>
    </div>
  );
}

function UserStatusRow({
  title,
  value,
  linkTo,
  tooltip,
  browserStatuses,
  handleChange,
}) {
  const [expandRow, setExpandRow] = useState(false);

  return (
    <>
      <div
        className={`row mt-2 ${linkTo ? 'user-status-row' : ''}`}
        onClick={() => {
          if (linkTo) {
            setExpandRow(!expandRow);
          }
        }}
      >
        {tooltip ? (
          <div className="col-xs-9 extension-status-title">
            <ToolTip toolTipText={tooltip} toolTipPosition="bottom">
              {title}
            </ToolTip>
          </div>
        ) : (
          <div className="col-xs-9 extension-status-title">{title}</div>
        )}
        <div className="col-xs-3 d-flex align-items-center">
          {handleChange ? (
            <Switch
              className=""
              offColor="#65737b"
              offHandleColor="#A4B4BD"
              onColor="#d5e7f4"
              onHandleColor="#1286C8"
              width={30}
              height={16}
              checkedIcon={false}
              uncheckedIcon={false}
              checked={value}
              onChange={(checked, event) => {
                event.preventDefault();
                event.stopPropagation();
                handleChange(checked);
              }}
            />
          ) : (
            renderCheck(value)
          )}
        </div>
      </div>
      {expandRow && (
        <div>
          <div className="row mt-1">
            <div className="col-xs-12 ml-2 font-size-12">{linkTo}</div>
          </div>
          {browserStatuses && (
            <>
              <div className="row mt-1">
                <div className="col-xs-6 ml-2 font-size-12">Chrome</div>
                <div className="col-xs-4 ml-2 font-size-12 text-center">
                  {renderBrowserStatus(browserStatuses, title, 'chrome')}
                </div>
              </div>
              <div className="row mt-1">
                <div className="col-xs-6 ml-2 font-size-12">Safari</div>
                <div className="col-xs-4 ml-2 font-size-12 text-center">
                  {renderBrowserStatus(browserStatuses, title, 'safari')}
                </div>
              </div>
              <div className="row mt-1">
                <div className="col-xs-6 ml-2 font-size-12">Edge</div>
                <div className="col-xs-4 ml-2 font-size-12 text-center">
                  {renderBrowserStatus(browserStatuses, title, 'edge')}
                </div>
              </div>
              <div className="row mt-1">
                <div className="col-xs-6 ml-2 font-size-12">Firefox</div>
                <div className="col-xs-4 ml-2 font-size-12 text-center">
                  {renderBrowserStatus(browserStatuses, title, 'firefox')}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

const ExtensionStatusRow = ({
  title,
  condition,
  linkTo,
  action,
  userName,
  extensionName,
  latestExtensions,
}) => {
  const browserStatuses = useMemo(() => {
    return latestExtensions.reduce((acc, ext) => {
      if (ext && ext.extension) {
        acc[ext.extension] = ext;
      }

      return acc;
    }, {});
  }, [latestExtensions]);

  return (
    <StatusRow
      title={title}
      value={condition}
      linkTo={
        condition ? (
          <>
            {userName ? `${userName} has` : 'You have'}{' '}
            {userName ? (
              <>{action} </>
            ) : (
              <Link to={linkTo} className="font-italic">
                {action}{' '}
              </Link>
            )}
            {!userName && 'your'} extension
          </>
        ) : (
          <>
            {userName || 'You'} should{' '}
            {userName ? (
              <>{action} </>
            ) : (
              <Link to={linkTo} className="font-italic">
                {action}{' '}
              </Link>
            )}
            {!userName && 'your'} extension
          </>
        )
      }
      tooltip={extensionName}
      browserStatuses={browserStatuses}
    />
  );
};

const StatusRow = ({
  title,
  value,
  linkTo,
  tooltip,
  browserStatuses,
  handleChange,
}) => (
  <UserStatusRow
    title={title}
    value={value}
    linkTo={linkTo}
    tooltip={tooltip}
    browserStatuses={browserStatuses}
    handleChange={handleChange}
  />
);

const FeatureRow = ({ title, setting, isOwner, handleChange }) => (
  <StatusRow
    title={title}
    value={setting}
    linkTo={
      setting || !isOwner ? null : (
        <>
          Turn this feature on in your <Link to="/settings">settings</Link>
        </>
      )
    }
    handleChange={isOwner ? handleChange : null}
  />
);

function renderBrowserStatus(browserStatuses, title, browser) {
  const key = title === 'Logged in' ? 'login' : 'pinned';
  const oppositeKeyTime =
    title === 'Logged in' ? 'loggedOutTime' : 'unpinnedTime';

  const extensionDetail = Object.values(browserStatuses).find(
    (extension) =>
      extension &&
      extension.extension &&
      extension.extension.toLowerCase().includes(browser),
  );

  if (!extensionDetail) {
    return <span className="font-size-10">Not installed</span>;
  }

  const time = extensionDetail[key] && extensionDetail[key].time;
  const oppositeActionTime =
    extensionDetail[key] && extensionDetail[key][oppositeKeyTime];

  if (time && !oppositeActionTime) {
    return renderCheckOnly(true);
  }

  if (!time && oppositeActionTime) {
    return renderCheckOnly(false);
  }

  if (time > oppositeActionTime) {
    return renderCheckOnly(true);
  }

  if (time < oppositeActionTime) {
    return renderCheckOnly(false);
  }

  return <span className="font-size-10">Not installed</span>;
}

function renderCheckOnly(checked) {
  if (checked) {
    return <Icon icon={ICONS.CHECK} size={10} className="av-green" />;
  }

  return <Icon icon={ICONS.CLOSE} size={10} className="av-red" />;
}
